import {useState, useEffect, useRef, RefObject} from 'react';

/**
 * Custom hook to get the bounding client rect of a referenced element.
 *
 * @return {[React.RefObject, {top: number, right: number, bottom: number, left: number}]}
 *          An array containing the ref object and the bounding rect object.
 *
 * @example
 * import React from 'react';
 * import useBoundingClientRect from './useBoundingClientRect';
 *
 * const MyComponent = () => {
 *     const [sectionRef, boundingRect] = useBoundingClientRect();
 *
 *     return (
 *         <div ref={sectionRef}>
 *             <p>Top: {boundingRect.top}</p>
 *             <p>Right: {boundingRect.right}</p>
 *             <p>Bottom: {boundingRect.bottom}</p>
 *             <p>Left: {boundingRect.left}</p>
 *         </div>
 *     );
 * };
 *
 * export default MyComponent;
 */

interface BoundingRect {
    top: number;
    right: number;
    bottom: number;
    left: number;
	height?: number;
	width?: number;
}

function useBoundingClientRect<T extends HTMLElement>(): [RefObject<T | null>, BoundingRect] {
	const sectionRef = useRef<T>(null);
	const [boundingRect, setBoundingRect] = useState<BoundingRect>({
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		height: 0,
		width: 0,
	});

	useEffect(() => {
		function updateRect() {
			if (sectionRef.current) {
				const rect = sectionRef.current.getBoundingClientRect();
				setBoundingRect({
					top: rect.top,
					right: rect.right,
					bottom: rect.bottom,
					left: rect.left,
					height: rect.height,
					width: rect.width,
				});
			}
		}

		updateRect();

		window.addEventListener('resize', updateRect);
		return () => window.removeEventListener('resize', updateRect);
	}, []);

	return [sectionRef, boundingRect];
}

export default useBoundingClientRect;